'use client'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useFormContext } from 'react-hook-form'

import { URL_MAX_LENGTH } from '@/constants'
import { useSyncFieldWithAtom } from '@/hooks'

import { RhcField } from '../../components/RhcField'
import { InputButton } from '../InputButton'
import { TFacebookButton, TFacebookInfoFields } from './FacebookInfoFormModel'

export const facebookButtonListAtom = atomWithReset<TFacebookButton[]>([])

const ButtonTextField = ({
	index,
	buttonList,
}: {
	index: number
	buttonList: TFacebookButton[]
}) => {
	const t = useTranslations()

	const form = useFormContext<TFacebookInfoFields>()

	const setButtonList = useSetAtom(facebookButtonListAtom)

	const setValue = (value?: string) => {
		if (value !== undefined) {
			setButtonList((prev) => {
				const newValue = [...prev]
				newValue[index]!.title = value
				return newValue
			})
		}
	}

	return (
		<RhcField
			type="text"
			form={form}
			path={`buttons.${index}.title`}
			value={buttonList[index]!.title}
			setValue={setValue}
			fieldName="button_title"
			fieldGroup="buttons"
			label={t(
				'generator__content_form_section__images_information__button_text__label',
			)}
			placeholder={t(
				'generator__content_form_section__images_information__button_text__placeholder',
			)}
			className="w-full tablet:grow"
		/>
	)
}

const ButtonUrlField = ({
	index,
	buttonList,
}: {
	index: number
	buttonList: TFacebookButton[]
}) => {
	const t = useTranslations()

	const form = useFormContext<TFacebookInfoFields>()

	const setButtonList = useSetAtom(facebookButtonListAtom)

	const setValue = (value?: string) => {
		if (value !== undefined) {
			setButtonList((prev) => {
				const newValue = [...prev]
				newValue[index]!.url = value
				return newValue
			})
		}
	}

	return (
		<RhcField
			type="text"
			form={form}
			path={`buttons.${index}.url`}
			value={buttonList[index]!.url}
			setValue={setValue}
			fieldName="button_url"
			fieldGroup="buttons"
			label={t(
				'generator__content_form_section__images_information__button_url__label',
			)}
			placeholder={t('ui__input_placeholders__url')}
			maxLength={URL_MAX_LENGTH + 1}
			className="grow tablet:w-full"
		/>
	)
}

export const ButtonsSection = () => {
	const { setValue, trigger } = useFormContext<TFacebookInfoFields>()

	const [buttonList, setButtonList] = useAtom(facebookButtonListAtom)

	useSyncFieldWithAtom({
		atom: facebookButtonListAtom,
		setFieldValue: (value) => setValue('buttons', value),
	})

	if (!buttonList.length) {
		return null
	}

	const handleClickRemove = (index: number) => {
		setButtonList((prev) => prev.filter((_, i) => i !== index))
		trigger('buttons')
	}

	return (
		<ul className="mt-6 flex flex-col gap-2 tablet:mt-8">
			{buttonList.map((_, index) => (
				<li
					key={index}
					className="flex flex-wrap gap-4 rounded-10 bg-light-grey-70 p-4 tablet:flex-nowrap tablet:p-6"
				>
					<ButtonTextField index={index} buttonList={buttonList} />
					<ButtonUrlField index={index} buttonList={buttonList} />
					<InputButton
						icon={<LineTrashAlt className="size-5" />}
						onClick={() => handleClickRemove(index)}
						className="mt-8"
					/>
				</li>
			))}
		</ul>
	)
}
